import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./StandbyPaywall.module.scss"

/**
 * STANDBY (`/standby`) — a self-contained EXPIRATION / win-back paywall for
 * Private Photo Vault. (Paywall type for this run was drawn at random → Expiration.)
 *
 * Concept: your Premium is *on standby*. Every premium system that guards your
 * library is sitting idle in a control console. One MASTER SWITCH brings them
 * all back LIVE — the system LEDs cascade amber→green, a "systems live" counter
 * ticks up, the big status word flips STANDBY→LIVE — and you resume from today
 * with nothing to re-import.
 *
 * Deliberately outside the saturated expiration cluster (warm-ivory-photographic
 * "kept through ‹month›" / whole-year light-flood, or Deep-Freeze cold fear):
 *   - Layout:      UI-native systems CONTROL CONSOLE (status readout · library-
 *                  intact strip · stack of system rows w/ LEDs · master switch ·
 *                  power module). Not a card / object-on-a-pedestal / grid /
 *                  receipt / door / gift-box / scope / ring / zip-case.
 *   - Interaction: FLIP THE MASTER SWITCH BACK ON — re-activation / power-restore,
 *                  status flipping OFF→ON with an LED cascade + counter tick-up.
 *                  Not a scrub / pour / dial / tear / stamp / wind / lift / reveal.
 *   - Register:    confident-CLINICAL with a wry, light touch — "paused, not lost."
 *   - Art:         flat control-console, justified-dark slate; brand BLUE = LIVE,
 *                  amber = PAUSED; geometric techy type. Photos only as a small
 *                  "library intact" strip — non-photographic hero.
 *   - Value:       "here's exactly what's paused — flip it back on, resume from
 *                  today, no re-import." Concrete features-going-dark checklist.
 *   - Motion:      NO grey→colour reveal, NO whole-year flood. Status OFF→ON,
 *                  LED cascade, counter tick, kinetic status word, switch travel.
 *
 * Honest / compliant: the whole thesis is "your library is untouched, only
 * premium *features* are paused." Yearly is the featured, most-prominent price;
 * monthly is smaller & secondary. No fake scarcity/countdown, no fabricated
 * personal counts, savings computed from the real debug prices.
 *
 * Concept doc: claude-paywalls/standby.md · ledger row #53.
 */

type Phase = "standby" | "booting" | "live"

// Real bundled memory thumbnails (public/recall/*.jpg — no runtime egress). Shown
// small, as proof the library is intact — never as the hero, never with a count.
const LIBRARY = [
  "sunset-1.jpg",
  "beach-2.jpg",
  "dog-1.jpg",
  "flower-2.jpg",
  "coffee-1.jpg",
  "mountain-3.jpg",
  "city-2.jpg",
  "food-1.jpg",
]

interface SystemDef {
  key: string
  name: string
  note: string
  icon: JSX.Element
}

const ic = (d: string, extra?: JSX.Element) => (
  <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden>
    <path
      d={d}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {extra}
  </svg>
)

// The six real Premium systems that go idle when a subscription lapses.
const SYSTEMS: SystemDef[] = [
  {
    key: "backup",
    name: "Cloud photo & video backup",
    note: "auto-copies every new memory",
    icon: ic(
      "M7 18a4 4 0 0 1 0-8 5 5 0 0 1 9.6-1.3A3.7 3.7 0 0 1 18 18H7Z M12 14.5v-3m0 0-1.6 1.6M12 11.5l1.6 1.6",
    ),
  },
  {
    key: "trash",
    name: "Deleted-photo recovery",
    note: "restore from Trash for 30 days",
    icon: ic(
      "M5 7h14M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7m-8 0 .8 11a1.6 1.6 0 0 0 1.6 1.5h5.2a1.6 1.6 0 0 0 1.6-1.5L18 7",
    ),
  },
  {
    key: "breakin",
    name: "Break-in photo alerts",
    note: "snaps anyone who tries a wrong PIN",
    icon: ic(
      "M12 3.5 5 6.2v5c0 4.2 3 6.9 7 8.3 4-1.4 7-4.1 7-8.3v-5L12 3.5Z",
      <circle
        key="c"
        cx="12"
        cy="11"
        r="1.9"
        fill="currentColor"
        stroke="none"
      />,
    ),
  },
  {
    key: "decoy",
    name: "Decoy vault",
    note: "a second PIN opens a stand-in album",
    icon: ic(
      "M4 9.5c1.6-1.2 4-1.2 5.6 0M14.4 9.5c1.6-1.2 4-1.2 5.6 0M4.5 13.5h15M8 17.5c1.2 1.3 6.8 1.3 8 0",
    ),
  },
  {
    key: "dupes",
    name: "Duplicate cleanup",
    note: "reclaims space from re-saved copies",
    icon: ic(
      "M9 9h9a1.5 1.5 0 0 1 1.5 1.5V19A1.5 1.5 0 0 1 18 20.5H9A1.5 1.5 0 0 1 7.5 19v-8.5A1.5 1.5 0 0 1 9 9Z M6 15.5A1.5 1.5 0 0 1 4.5 14V5.5A1.5 1.5 0 0 1 6 4h8.5A1.5 1.5 0 0 1 16 5.5",
    ),
  },
  {
    key: "originals",
    name: "Full-quality originals",
    note: "keeps every pixel, never compressed",
    icon: ic(
      "M4.5 5.5h15A1.5 1.5 0 0 1 21 7v10a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17V7a1.5 1.5 0 0 1 1.5-1.5Z M3.4 16l4.3-4.3a1.5 1.5 0 0 1 2.1 0l3.4 3.4m-1.3-1.3 1.9-1.9a1.5 1.5 0 0 1 2.1 0L21 14.6",
    ),
  },
]

const StandbyPaywall = () => {
  const { state } = useAppState()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    return m ? Math.round((1 - y / (m * 12)) * 100) : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [phase, setPhase] = useState<Phase>("standby")
  const [liveCount, setLiveCount] = useState(0)
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  const didAutoPlay = useRef(false)

  const clearTimers = () => {
    timers.current.forEach(clearTimeout)
    timers.current = []
  }

  // Flip the master switch → boot the systems one by one, then settle LIVE.
  const bringOnline = useCallback(() => {
    setPhase((p) => {
      if (p !== "standby") return p
      clearTimers()
      setLiveCount(0)
      // stagger the LED cascade — one system every ~150ms
      SYSTEMS.forEach((_, i) => {
        timers.current.push(
          setTimeout(() => setLiveCount(i + 1), 220 + i * 155),
        )
      })
      timers.current.push(
        setTimeout(() => setPhase("live"), 220 + SYSTEMS.length * 155 + 140),
      )
      native.logMixpanelEvent("html_standby_online", {})
      return "booting"
    })
  }, [])

  const reset = () => {
    clearTimers()
    setLiveCount(0)
    setPhase("standby")
    native.logMixpanelEvent("html_standby_reset", {})
  }

  // Auto-demonstrate the reactivation once, shortly after mount, so a passive
  // viewer sees the whole STANDBY→LIVE payoff without touching anything.
  useEffect(() => {
    if (didAutoPlay.current) return
    didAutoPlay.current = true
    const t = setTimeout(() => bringOnline(), 1050)
    timers.current.push(t)
    return clearTimers
  }, [bringOnline])

  const live = phase === "live"
  const on = phase !== "standby"

  const toggleSwitch = () => {
    if (phase === "standby") bringOnline()
    else if (live) reset()
  }

  const onSwitchKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      toggleSwitch()
    }
  }

  const choosePlan = (key: "yearly" | "monthly") => {
    if (key === plan) return
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }

  const handleClose = () => native.postNativeEvent("close")

  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    native.logMixpanelEvent("html_standby_purchase", { plan })
    if (product) native.subscribe(product)
  }

  return (
    <div className={`${styles.page} ${styles[phase]}`}>
      <div className={styles.glow} aria-hidden />
      <div className={styles.scan} aria-hidden />

      {/* ── Header: brand lockup + close ─────────────────────────────── */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden>
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.5 4.5 6v6c0 4.6 3.2 7.9 7.5 9.4 4.3-1.5 7.5-4.8 7.5-9.4V6L12 2.5Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <rect
                x="8.5"
                y="11"
                width="7"
                height="6"
                rx="1.4"
                fill="currentColor"
              />
              <path
                d="M9.6 11V9.3a2.4 2.4 0 0 1 4.8 0V11"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>
          </span>
          <span className={styles.brandName}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* ── Status readout: the kinetic STANDBY → LIVE word ──────────── */}
      <section className={styles.readout}>
        <span className={styles.statusChip}>
          <i className={styles.chipDot} aria-hidden />
          {live
            ? "All systems live"
            : on
              ? "Bringing systems online…"
              : "Premium on standby"}
        </span>
        <h1 className={styles.status}>
          <span className={styles.wordStandby}>STANDBY</span>
          <span className={styles.wordLive}>LIVE</span>
        </h1>
        <p className={styles.sub}>
          {live
            ? "Your vault's premium systems are running again — picking up right from today."
            : "Everything's still here. The premium guards just clocked off. Flip them back on below."}
        </p>
      </section>

      {/* ── Library-intact strip: the no-data-loss anchor ────────────── */}
      <section className={styles.library}>
        <div className={styles.libStrip}>
          {LIBRARY.map((file, i) => (
            <span
              className={styles.thumb}
              key={file}
              style={{ ["--i" as any]: i }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/recall/${file}`}
                alt=""
                aria-hidden
              />
            </span>
          ))}
          <span className={styles.libSeal} aria-hidden>
            <svg viewBox="0 0 24 24" width="13" height="13">
              <path
                d="M20 7 10 17l-5-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
        <p className={styles.libNote}>
          Your library is <strong>safe &amp; untouched</strong> — nothing was
          ever deleted. Only the premium features below are paused.
        </p>
      </section>

      {/* ── Systems console: the features that go idle ───────────────── */}
      <section className={styles.console}>
        <div className={styles.consoleHead}>
          <span className={styles.consoleTitle}>Premium systems</span>
          <span className={styles.counter}>
            <b className={styles.counterNum}>{liveCount}</b>
            <span aria-hidden>/</span>
            {SYSTEMS.length} live
          </span>
        </div>

        <ul className={styles.rows}>
          {SYSTEMS.map((sysd, i) => {
            const isLive = i < liveCount
            return (
              <li
                key={sysd.key}
                className={`${styles.row} ${isLive ? styles.rowLive : ""}`}
                style={{ ["--i" as any]: i }}
              >
                <span className={styles.rowIcon} aria-hidden>
                  {sysd.icon}
                </span>
                <span className={styles.rowText}>
                  <span className={styles.rowName}>{sysd.name}</span>
                  <span className={styles.rowNote}>{sysd.note}</span>
                </span>
                <span className={styles.led} aria-hidden>
                  <i className={styles.ledDot} />
                  <span className={styles.ledLabel}>
                    {isLive ? "LIVE" : "PAUSED"}
                  </span>
                </span>
              </li>
            )
          })}
        </ul>
      </section>

      {/* ── Master switch: the re-activation heart ───────────────────── */}
      <section
        className={`${styles.master} ${on ? styles.masterOn : ""}`}
        role="switch"
        aria-checked={on}
        aria-label="Bring all premium systems back online"
        tabIndex={0}
        onClick={toggleSwitch}
        onKeyDown={onSwitchKey}
      >
        <span className={styles.masterText}>
          <span className={styles.masterLabel}>System power</span>
          <span className={styles.masterState}>
            {live
              ? "All systems live · tap to replay"
              : on
                ? "Powering up…"
                : "Tap to bring Premium back online"}
          </span>
        </span>
        <span className={styles.toggle} aria-hidden>
          <span className={styles.toggleKnob}>
            <span className={styles.knobIcon}>
              <svg viewBox="0 0 24 24" width="15" height="15">
                <path
                  d="M12 3.5v7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
                <path
                  d="M6.8 6.9a7 7 0 1 0 10.4 0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.toggleOff}>OFF</span>
          <span className={styles.toggleOn}>ON</span>
        </span>
      </section>

      {/* ── Power module (pricing): yearly featured, monthly secondary ─ */}
      <section className={styles.plans}>
        <button
          className={`${styles.yearCard} ${plan === "yearly" ? styles.yearOn : ""}`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearRadio} aria-hidden />
          <span className={styles.yearMain}>
            <span className={styles.yearName}>
              Yearly power
              <span className={styles.recommend}>Recommended</span>
            </span>
            <span className={styles.yearSub}>
              Keeps every system running all year · ≈ {yearlyPerMonth}/mo
            </span>
          </span>
          <span className={styles.yearPrice}>
            <b>{yearlyPrice}</b>
            <em>/year</em>
          </span>
          {savingsPct > 0 && (
            <span className={styles.saveBadge}>Save {savingsPct}%</span>
          )}
        </button>

        <button
          className={`${styles.monthRow} ${plan === "monthly" ? styles.monthOn : ""}`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthRadio} aria-hidden />
          <span className={styles.monthName}>Monthly power</span>
          <span className={styles.monthPrice}>{monthlyPrice}/mo</span>
        </button>
      </section>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaMain}>Bring everything back online</span>
        <span className={styles.ctaSub}>
          {plan === "yearly"
            ? hasTrial
              ? `${trialDays} days free, then ${yearlyPrice}/year (≈ ${yearlyPerMonth}/mo)`
              : `${yearlyPrice}/year (≈ ${yearlyPerMonth}/mo)`
            : `${monthlyPrice}/mo`}
        </span>
      </button>

      <p className={styles.reassure}>
        Resumes from today · Auto-renews until canceled · Cancel anytime · Your
        photos are always yours
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default StandbyPaywall
